import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { InvitationRead, InvitationService } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../components/dialog';
import { Heading } from '../../../components/heading';
import { ListFilter } from '../../../components/list-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { EditInvitationPermissionsDialog } from './EditInvitationPermissionsDialog';
import PendingInvitationCard from './PendingInvitationCard';
import { ORGANIZATION_PENDING_INVITATIONS_LIST_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 21;
const DATE_FORMAT = 'LLL d, yyyy';

type ApiParams = Parameters<typeof InvitationService.invitationList>[0];
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  HealthcareEntityRepGroups: 'groupNamesIn',
};
const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  body: string;
  confirmText: string;
  confirmVariant?: 'danger' | 'primary';
  isLoading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmText,
  confirmVariant = 'primary',
  isLoading = false,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogBody>
      <p className="tw:text-zinc-700 tw:dark:text-zinc-300">{body}</p>
    </DialogBody>
    <DialogActions>
      <Button type="button" plain onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button
        type="button"
        color={confirmVariant === 'danger' ? 'stoplight-danger' : 'brandgreen1'}
        onClick={onConfirm}
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

const ActionCell: React.FC<{ invitation: InvitationRead }> = ({ invitation }) => {
  const queryClient = useQueryClient();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const resendMutation = useMutation({
    mutationFn: async () => {
      await InvitationService.invitationResendUpdate({ id: invitation.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_PENDING_INVITATIONS_LIST_QUERY_KEY],
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      await InvitationService.invitationDestroy({ id: invitation.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_PENDING_INVITATIONS_LIST_QUERY_KEY],
      });
    },
  });

  const handleResend = async () => {
    try {
      await resendMutation.mutateAsync();
      toast.success('Invitation resent successfully');
      setShowResendDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync();
      toast.success('Invitation canceled successfully');
      setShowCancelDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const email = invitation.email || 'this user';

  return (
    <>
      <div className="tw:flex tw:justify-start tw:gap-2">
        <TooltippedText tooltipContent="Resend">
          <Button
            small
            color="brand-green-outline"
            onClick={(e) => {
              e.stopPropagation();
              setShowResendDialog(true);
            }}
            title="Resend"
          >
            <i className="far fa-fw fa-paper-plane" />
          </Button>
        </TooltippedText>
        <TooltippedText tooltipContent="Edit">
          <Button
            small
            color="brand-green-outline"
            onClick={(e) => {
              e.stopPropagation();
              setShowEditDialog(true);
            }}
            title="Edit"
          >
            <i className="far fa-fw fa-user-lock" />
          </Button>
        </TooltippedText>
        <TooltippedText tooltipContent="Cancel">
          <Button
            small
            color="stoplight-danger-outline"
            onClick={(e) => {
              e.stopPropagation();
              setShowCancelDialog(true);
            }}
            title="Cancel"
          >
            <i className="far fa-fw fa-trash-can" />
          </Button>
        </TooltippedText>
      </div>

      <EditInvitationPermissionsDialog
        invitation={invitation}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />

      <ConfirmationDialog
        open={showResendDialog}
        onClose={() => setShowResendDialog(false)}
        onConfirm={handleResend}
        title="Resend Invitation"
        body={`Are you sure you want to resend the invitation to ${email}?`}
        confirmText="Resend Invitation"
        isLoading={resendMutation.isPending}
      />

      <ConfirmationDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancel}
        title="Undo Invitation"
        body={`Are you sure you want to cancel the invitation to ${email}?`}
        confirmText="Undo Invitation"
        confirmVariant="danger"
        isLoading={cancelMutation.isPending}
      />
    </>
  );
};

export const PendingInvitationsList: React.FC = () => {
  useDocumentTitle('Pending Invitations');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const displayTypePrefKey = 'OrganizationPendingInvitations';
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const displayType = (filterValues.displayType || 'card') as 'card' | 'table';

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [ORGANIZATION_PENDING_INVITATIONS_LIST_QUERY_KEY, search, categoryFilters],
    [search, categoryFilters]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) => {
        return InvitationService.invitationList({
          search: search || undefined,
          ...categoryApiParams,
          limit: PAGE_SIZE,
          offset: pageParam as number,
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.next) {
          return allPages.length * PAGE_SIZE;
        }
        return undefined;
      },
    });

  const invitations = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreInvitations = React.useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreInvitations, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const { data: groupNamesMap = {} } = useCodeDefinitionsAsMap('HealthcareEntityRepGroups');

  return (
    <main className={MAIN_CONTENT_AREA}>
      <div className={STICKY_HEADER}>
        <div className={PAGE_HEADER}>
          <Heading level={1} className="tw:!text-brand-green-0 tw:dark:!text-zinc-100">
            <i className="far fa-fw fa-envelope tw:mr-1" />
            Pending Invitations
          </Heading>
          <div className="tw:flex tw:gap-2">
            <Button color="brandgreen1" onClick={() => navigate('/app/organization/users')}>
              Users List
            </Button>
            <Button color="brandgreen1" onClick={() => console.log('Invite User clicked')}>
              Invite User
            </Button>
          </div>
        </div>

        {isError && (
          <div className="tw:mt-4 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
            <p className="tw:font-semibold">Something went wrong</p>
            <p className="tw:text-sm">
              Please try again later. If the problem persists, please contact us.
            </p>
          </div>
        )}

        <div className="tw:mt-1">
          <ListFilter
            show_search={true}
            categories={Array.from(CATEGORIES)}
            showStatus={false}
            showReason={false}
            showDisplayType={true}
            labelOverrides={{ HealthcareEntityRepGroups: 'Permissions' }}
            sortOptions={[]}
            values={filterValues}
            onFilterChange={updateFilters}
          />
        </div>
      </div>

      <div className="tw:mt-3">
        {isLoading && invitations.length === 0 && (
          <div className="tw:text-center tw:py-8">Loading invitations...</div>
        )}
        {!isLoading && invitations.length === 0 ? (
          <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
            No pending invitations.
            <div className="tw:text-zinc-500 tw:italic tw:text-sm">
              Invite someone to get started.
            </div>
          </div>
        ) : (
          <>
            {invitations.length > 0 && (
              <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                Showing {invitations.length} of {totalCount} invitations
              </div>
            )}

            {displayType === 'card' ? (
              <div className="tw:@container">
                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                  {invitations.map((invitation) => (
                    <div key={invitation.id}>
                      <PendingInvitationCard
                        invitation={invitation}
                        groupNamesMap={groupNamesMap}
                        actions={<ActionCell invitation={invitation} />}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <Table striped dense>
                <TableHead>
                  <TableHeader>Email</TableHeader>
                  <TableHeader className="tw:w-[500px] tw:max-w-[500px]">Permissions</TableHeader>
                  <TableHeader>Invited On</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell>{invitation.email || '—'}</TableCell>
                      <TableCell>
                        <div className="tw:w-[500px] tw:max-w-[500px] tw:truncate">
                          {invitation.group_names
                            ?.map((code) => groupNamesMap[code] || code)
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {invitation.created_at
                          ? format(parseISO(invitation.created_at), DATE_FORMAT)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <ActionCell invitation={invitation} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {hasNextPage && (
              <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                {isFetchingNextPage && (
                  <div className="tw:text-center tw:text-zinc-500">Loading more invitations...</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};
